import {Injectable} from '@angular/core';
import {EmiUtil} from '../utils/emi.util';
import {flatten} from '@angular/compiler';
import {MathUtil} from '../utils/math.util';
import {EmiScheduleGenerator} from '@finzy/node-emi-calculator';
import {CashFlow, CashFlowGenerator} from '../utils/cash-flow-generator';
import {convertRate, RateInterval, xirr, XirrInput} from '@appraised/node-irr';
import * as moment from 'moment';

@Injectable()
export class XirrCalculator {
    private static readonly XirrLibraryDateFormat = 'YYYYMMDD';
    private readonly emiUtil: EmiUtil;
    private readonly cashFlowGenerator: CashFlowGenerator;
    private readonly emiScheduleGenerator: EmiScheduleGenerator;

    constructor(emiScheduleGenerator: EmiScheduleGenerator, emiUtil: EmiUtil, cashFlowGenerator: CashFlowGenerator) {
        this.emiUtil = emiUtil;
        this.cashFlowGenerator = cashFlowGenerator;
        this.emiScheduleGenerator = emiScheduleGenerator;
    }

    public async calculateForInvestments(investments: { investmentAmount: number, tenure: number, roi: number }[]): Promise<number> {
        const assumedDisbursalDate = moment().add(7, 'days').toDate();
        const firstEmiDate = this.emiUtil.getFirstEmiDate(assumedDisbursalDate);
        const emiSchedules = await Promise.all(investments.map(investment => {
            return this.emiScheduleGenerator.generateFor(investment.investmentAmount, investment.tenure, investment.roi,
                assumedDisbursalDate, firstEmiDate);
        }));
        const emiCashFlows = flatten(emiSchedules.map(emiSchedule => this.cashFlowGenerator.generateForEmiSchedule(emiSchedule)));
        const investmentCashFlows = investments.map(investment => this.cashFlowGenerator.generateForInvestment(investment.investmentAmount, moment().toDate()));
        return this.calculateXirr([...emiCashFlows, ...investmentCashFlows]);
    }

    private calculateXirr(cashFlows: CashFlow[]) {
        const allXirrInputs = cashFlows.map(cashFlow => {
            return <XirrInput>{amount: cashFlow.amount, date: moment(cashFlow.date).format(XirrCalculator.XirrLibraryDateFormat)};
        });
        if (allXirrInputs.length === 0) {
            return 0;
        }
        const result = xirr(allXirrInputs);
        return MathUtil.roundOff(convertRate(result.rate, RateInterval.Year) * 100);
    }
}